import { useCallback, useEffect } from 'react';
import { RoiType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { featureFlags } from '../../../feature-flags';
import { trackingIds } from '../../../tracking-ids';
import { useFetchMessagingROIOOverview } from '../hooks/use-fetch-messaging-roi-overview';
import { useFetchRoiTrendLine } from '../hooks/use-fetch-roi-trend-line';
import { useHasRoiFeature } from '../hooks/use-has-roi-feature';
import { WidgetCardProps } from './types';
import { WidgetCard } from './widget-card';

export const MCTWidgetCard = ({ handleUpdateActiveCards, showTrendLine, timePeriod }: WidgetCardProps) => {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.missedCallTextRoiDemoData);
  const { isFeatureEnabled: isMCTRoiEligibleLocation } = useHasRoiFeature({ roi: 'missedCallText' });

  const payload = {
    additionalRequestPayload: {
      roiType: RoiType.MISSED_CALL_TEXT,
    },
    filters: {
      startDate: timePeriod.value.startDate,
      endDate: timePeriod.value.endDate,
      locationIds: selectedLocationIds,
    },
    isDemoAccount,
    isEnabled: isMCTRoiEligibleLocation,
  };

  const { aggregated, isLoading } = useFetchMessagingROIOOverview(payload);

  const appointmentsScheduled = aggregated?.patientsScheduled?.appointmentsCount || 0;

  const { isLoading: isLoadingTrendLine, trendLine } = useFetchRoiTrendLine({
    ...payload,
    isEnabled: payload.isEnabled && showTrendLine,
  });

  const handleNavigate = useCallback(() => {
    navigate({
      to: '/analytics/roi/missed-call-text-conversion',
    });
  }, []);

  useEffect(() => {
    handleUpdateActiveCards('missedCallText', !!appointmentsScheduled);
  }, [appointmentsScheduled]);

  if (!appointmentsScheduled) {
    return null;
  }

  return (
    <WidgetCard
      iconName='message-small'
      isLoading={isLoading || isLoadingTrendLine}
      onClick={handleNavigate}
      subTitle={t('Appointments Scheduled')}
      title={t('Missed Call Text')}
      trackingId={trackingIds.roi.missedCallTextRoiWidget}
      trend={showTrendLine && trendLine}
      value={formatters.value(appointmentsScheduled)}
    />
  );
};
